"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.defaultEditState = exports.basicFilters = void 0;
exports.exportEditedImage = exportEditedImage;
exports.getFilterCss = getFilterCss;
exports.mergeEditState = mergeEditState;
var _cropUtils = require("./cropUtils");
const defaultEditState = exports.defaultEditState = {
  mode: 'basic',
  filter: 'normal',
  adjustments: {
    saturation: 100,
    hue: 0,
    brightness: 100
  },
  crop: {
    aspect: '5x5',
    orientation: 'portrait',
    size: 100,
    x: 50,
    y: 50
  }
};
const basicFilters = exports.basicFilters = [{
  id: 'normal',
  label: 'Normal',
  css: 'none'
}, {
  id: 'mono',
  label: 'Mono',
  css: 'grayscale(1)'
}, {
  id: 'warm',
  label: 'Warm',
  css: 'sepia(0.25) saturate(1.2) hue-rotate(-8deg)'
}, {
  id: 'cool',
  label: 'Cool',
  css: 'saturate(1.1) hue-rotate(10deg)'
}, {
  id: 'contrast',
  label: 'Contrast',
  css: 'contrast(1.2) saturate(1.1)'
}, {
  id: 'print',
  label: 'Print Boost',
  css: 'contrast(1.08) brightness(1.04) saturate(1.14)'
}];
function getFilterCss(editState) {
  if (editState.mode === 'advanced') {
    const {
      saturation,
      hue,
      brightness
    } = editState.adjustments;
    return `saturate(${saturation}%) hue-rotate(${hue}deg) brightness(${brightness}%)`;
  }
  return basicFilters.find(filter => filter.id === editState.filter)?.css || 'none';
}
function mergeEditState(editState) {
  return {
    ...defaultEditState,
    ...editState,
    adjustments: {
      ...defaultEditState.adjustments,
      ...migrateRgbToAdjustments(editState),
      ...(editState?.adjustments || {})
    },
    crop: (0, _cropUtils.clampCropToAspect)({
      ...defaultEditState.crop,
      ...(editState?.crop || {})
    })
  };
}
async function exportEditedImage(image, editState) {
  if (!image?.previewUrl) {
    throw new Error('The image cannot be edited because its preview URL is missing.');
  }
  const img = await loadImage(image.previewUrl);
  const state = mergeEditState(editState);
  const source = getSourceCrop(img, state.crop);
  const canvas = document.createElement('canvas');
  const outputWidth = 1600;
  canvas.width = outputWidth;
  canvas.height = Math.round(outputWidth / source.aspect);
  const ctx = canvas.getContext('2d');
  ctx.filter = getFilterCss(state);
  ctx.drawImage(img, source.sx, source.sy, source.sw, source.sh, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(result => {
      if (!result) {
        reject(new Error('The edited image could not be exported. Check that Firebase Storage CORS is configured for this admin URL.'));
        return;
      }
      resolve(result);
    }, 'image/jpeg', 0.92);
  });
  return {
    blob,
    url: URL.createObjectURL(blob)
  };
}
function getSourceCrop(img, crop) {
  const box = (0, _cropUtils.getCropBox)(crop, img.naturalWidth / img.naturalHeight);
  return {
    aspect: box.aspect,
    sx: img.naturalWidth * (box.left / 100),
    sy: img.naturalHeight * (box.top / 100),
    sw: img.naturalWidth * (box.width / 100),
    sh: img.naturalHeight * (box.height / 100)
  };
}
function migrateRgbToAdjustments(editState) {
  if (!editState?.rgb) {
    return {};
  }
  const {
    red = 0,
    green = 0,
    blue = 0
  } = editState.rgb;
  return {
    saturation: Math.round(Math.max(20, 100 + (red + green + blue) / 3)),
    hue: Math.round((red - blue) * 0.5),
    brightness: Math.round(Math.max(40, 100 + green))
  };
}
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`The image could not be loaded for editing. The signed URL may be expired or Firebase Storage CORS may not allow this admin URL: ${getSafeUrlForMessage(src)}`));
    img.src = src;
  });
}
function getSafeUrlForMessage(src) {
  try {
    const url = new URL(src);
    return `${url.origin}${url.pathname}`;
  } catch {
    return 'invalid image URL';
  }
}